import { AbstractControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';

/**
 * Decide si un control debe mostrar su mensaje de error.
 * Política Perrón: errores visibles tras `touched` (blur) o `dirty`
 * (al editar). No mostramos errores en pristine para no agobiar al
 * usuario antes de que interactúe.
 */
export function shouldShowError(
  control: AbstractControl | null | undefined,
): boolean {
  if (!control) return false;
  return control.invalid && (control.touched || control.dirty);
}

/**
 * Devuelve el mensaje de error a renderizar para un control, o cadena
 * vacía si no hay error que mostrar. Mensajes en castellano de España,
 * tono Perrón (cercano pero claro, sin tecnicismos).
 */
export function errorMessageFor(
  control: AbstractControl | null | undefined,
  fieldLabel = 'Este campo',
): string {
  if (!shouldShowError(control)) return '';
  const errors = control!.errors;
  if (!errors) return '';

  if (errors['required']) return `${fieldLabel} es campo obligatorio.`;
  if (errors['email']) return 'Introduce un email válido.';
  if (errors['minlength']) return `${fieldLabel} es demasiado corto.`;
  if (errors['maxlength']) return `${fieldLabel} es demasiado largo.`;
  return 'Revisa este campo.';
}

/**
 * Aplica o quita validators a un grupo de controles dentro de un
 * FormGroup, en función de una condición (ej. delivery method).
 *
 * Uso típico: en un `valueChanges` del método de entrega, pasar la
 * lista de paths del subgroup `delivery.address` y la condición
 * "este método requiere dirección". El helper limpia validators de
 * los campos no requeridos para que sus errores no sigan colgando
 * cuando el user cambia de método.
 */
export function setRequiredOnControls(
  group: FormGroup,
  controlNames: string[],
  required: boolean,
  extraValidators: Record<string, ValidatorFn[]> = {},
): void {
  for (const name of controlNames) {
    const control = group.get(name);
    if (!control) continue;

    const validators = required
      ? [Validators.required, ...(extraValidators[name] ?? [])]
      : [...(extraValidators[name] ?? [])];

    control.setValidators(validators.length > 0 ? validators : null);
    control.updateValueAndValidity({ emitEvent: false });
  }
}
